import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  Input,
} from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  standalone: true,
  selector: 'app-car-camera-modal',
  imports: [IonicModule, CommonModule, ImageCropperComponent, TranslateModule],
  templateUrl: './car-camera-modal.component.html',
  styleUrls: ['./car-camera-modal.component.scss'],
})
export class CarCameraModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('video') video?: ElementRef<HTMLVideoElement>;
  capturedPhoto: string | null = null;
  private stream: MediaStream | null = null;
  facingMode: 'user' | 'environment' = 'environment';
  @Input() photoType:
    | 'car'
    | 'cars'
    | 'profile'
    | 'license'
    | 'identity'
    | 'driver_license' = 'car';
  @Input() galleryFile?: File;
  @Input() aspectRatio?: false;

  finalImage: string | null = null;
  tempCroppedImage: string | null = null;
  isCropping = false;
  cameraError = false;
  shutterFlash = false;

  constructor(private modalCtrl: ModalController) {}

  ngAfterViewInit() {
    if (this.photoType === 'profile') {
      this.facingMode = 'user';
    }

    if (this.galleryFile) {
      this.loadGalleryImage(this.galleryFile);
    } else {
      this.startCamera();
    }
  }

  get isGalleryMode(): boolean {
    return !!this.galleryFile;
  }

  get isCardFrame(): boolean {
    return (
      this.photoType === 'license' ||
      this.photoType === 'identity' ||
      this.photoType === 'driver_license'
    );
  }

  get cameraTitleKey(): string {
    if (this.photoType === 'profile') return 'CAMERA.TITLE_PROFILE';
    if (this.photoType === 'identity') return 'CAMERA.TITLE_ID';
    if (this.photoType === 'license' || this.photoType === 'driver_license') {
      return 'CAMERA.TITLE_LICENSE';
    }
    return 'CAMERA.TITLE_CAR';
  }

  get cameraHintKey(): string {
    if (this.photoType === 'profile') return 'CAMERA.ALIGN_PROFILE';
    return this.isCardFrame ? 'CAMERA.ALIGN_DOCUMENT' : 'CAMERA.ALIGN_VEHICLE';
  }
  loadGalleryImage(file: File) {
    const reader = new FileReader();

    reader.onload = () => {
      this.capturedPhoto = reader.result as string;
      this.finalImage = this.capturedPhoto; // 🔥 THIS LINE
      this.isCropping = false;
    };

    reader.readAsDataURL(file);
  }

  ngOnDestroy() {
    this.stopCamera();
  }

  private async startCamera() {
    this.cameraError = false;
    try {
      const videoEl = await this.waitForVideo();
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: this.facingMode } },
        });
      } catch (err) {
        console.warn('Exact facingMode failed, retrying without exact', err);
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: this.facingMode },
        });
      }
      videoEl.srcObject = this.stream;
      await videoEl.play();
    } catch (err) {
      console.error('Unable to start camera', err);
      this.cameraError = true;
    }
  }

  private waitForVideo(): Promise<HTMLVideoElement> {
    const existing = this.video?.nativeElement;
    if (existing) return Promise.resolve(existing);

    return new Promise((resolve, reject) => {
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        const el = this.video?.nativeElement;
        if (el) {
          clearInterval(timer);
          resolve(el);
        } else if (attempts > 20) {
          clearInterval(timer);
          reject(new Error('Video element not ready'));
        }
      }, 50);
    });
  }

  private stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
  }

  retake() {
    this.capturedPhoto = null;
    this.finalImage = null;
    this.tempCroppedImage = null;
    this.isCropping = false;
    this.cameraError = false;
    setTimeout(() => this.startCamera(), 0);
  }

  dismiss() {
    this.stopCamera();
    this.modalCtrl.dismiss(null, 'dismissed');
  }

  async flipCamera() {
    this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
    this.stopCamera();
    await this.startCamera();
  }

  takePhoto() {
    const video = this.video?.nativeElement;
    if (!video) return;

    this.shutterFlash = true;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')!.drawImage(video, 0, 0);
    this.capturedPhoto = canvas.toDataURL('image/jpeg');
    this.finalImage = this.capturedPhoto;
    this.stopCamera();
    setTimeout(() => {
      this.shutterFlash = false;
    }, 180);
  }

  startCrop() {
    this.isCropping = true;
  }

  onImageCropped(event: ImageCroppedEvent) {
    // Use objectUrl as a fallback if base64 is missing,
    // or simply ensure we are catching the base64 string
    this.tempCroppedImage = event.base64 || event.objectUrl || null;
    console.log('Cropped image updated');
  }

  confirmCrop() {
    console.log('Attempting to confirm crop...');

    if (!this.tempCroppedImage) {
      // If it's null, the user might have clicked too fast.
      console.warn('NO CROPPED IMAGE READY YET');
      return;
    }

    this.finalImage = this.tempCroppedImage;
    this.isCropping = false;
    // Note: Your save() function expects a dataURL string.
  }

  async save() {
    if (!this.finalImage) return;

    let file: File | null = null;

    if (this.finalImage.startsWith('data:')) {
      // It's a Base64 string
      file = this.dataURLtoFile(this.finalImage, 'photo.jpg');
    } else if (this.finalImage.startsWith('blob:')) {
      // It's an Object URL (Blob)
      const response = await fetch(this.finalImage);
      const blob = await response.blob();
      file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
    }

    if (file) {
      this.modalCtrl.dismiss({ file, preview: this.finalImage }, 'saved');
    } else {
      console.error('Could not generate file from image source');
    }
  }

  // Add this property to your class variables
  cropperReady = false;

  // Add these methods to your class
  imageLoaded() {
    this.cropperReady = true;
    console.log('Image loaded into cropper');
  }

  loadImageFailed() {
    console.error('Image failed to load in cropper');
    // You could show an alert here to the user
  }

  private dataURLtoFile(dataUrl: string, filename: string): File | null {
    try {
      const arr = dataUrl.split(',');
      if (arr.length < 2) return null; // Safety check

      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch) return null; // Safety check

      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    } catch (e) {
      console.error('Error converting dataURL to file', e);
      return null;
    }
  }
}
